import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ARTICLES } from "@/data/content";
import { useSEO } from "@/lib/seo";
import CTASection from "@/components/CTASection";

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  useSEO({
    title: article ? article.title : "Article",
    description: article ? article.excerpt : "",
    path: `/resources/${slug}`,
    jsonLd: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          author: { "@type": "Organization", name: "Wasserlab" },
          publisher: { "@type": "Organization", name: "Wasserlab" },
        }
      : null,
  });

  if (!article) return <Navigate to="/resources" replace />;

  return (
    <>
      <article className="bg-white">
        <header className="border-b border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-3xl px-5 lg:px-8 py-14 lg:py-16">
            <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-navy-900 transition-colors" data-testid="back-to-resources">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All Resources
            </Link>
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-emerald-600">{article.category}</p>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-navy-900">{article.title}</h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">{article.excerpt}</p>
            <p className="mt-4 text-sm font-semibold text-slate-500">{article.read}</p>
          </div>
        </header>

        <div className="mx-auto max-w-3xl px-5 lg:px-8 py-14">
          {article.sections.map((s, i) => (
            <section key={i} className="mb-10" data-testid={`article-section-${i}`}>
              <h2 className="font-heading text-2xl font-bold text-navy-900">{s.h}</h2>
              <p className="mt-3 text-base leading-relaxed text-slate-700">{s.p}</p>
            </section>
          ))}

          <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-6">
            <p className="text-sm leading-relaxed text-slate-700">
              This article is provided for general educational purposes only and does not constitute legal or regulatory
              advice. Wasserlab provides technical and compliance preparation support; final determinations and
              submissions remain the responsibility of the client under applicable requirements.
            </p>
          </div>

          <div className="mt-10 rounded-xl bg-navy-900 p-8 text-center noise-overlay">
            <h3 className="font-heading text-xl font-bold text-white">Have questions about your Tier II obligations?</h3>
            <Link
              to="/free-tier-ii-assessment"
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-amber-500 px-6 py-3 text-sm font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400"
              data-testid="article-cta-assessment"
            >
              Get a Free Tier II Assessment <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </article>

      <section className="bg-slate-50 py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-navy-900">Related resources</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/resources/${a.slug}`}
                className="group flex flex-col rounded-xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                data-testid={`related-article-${a.slug}`}
              >
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-600">{a.category}</span>
                <h3 className="mt-3 font-heading text-base font-bold text-navy-900 group-hover:text-amber-600 transition-colors">{a.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
